import { useRef, useCallback } from 'react';

/**
 * Trigger haptic feedback if supported by the device.
 * @param {'light'|'medium'|'heavy'} type
 */
export const triggerHaptic = (type = 'light') => {
  if (!navigator?.vibrate) return;
  const patterns = { light: 10, medium: 25, heavy: 50 };
  navigator.vibrate(patterns?.[type] || 10);
};

/**
 * useTouchSwipe — attach to a card element to detect left/right swipes.
 *
 * @param {object} options
 * @param {() => void} options.onSwipeLeft   - Called on left swipe (e.g. "Missed")
 * @param {() => void} options.onSwipeRight  - Called on right swipe (e.g. "Good")
 * @param {() => void} [options.onSwipeUp]   - Called on upward swipe (e.g. "Hard")
 * @param {number}     [options.threshold]   - Minimum px to count as a swipe (default 60)
 * @returns {{ handlers: object, swipeDelta: React.MutableRefObject }}
 */
const useTouchSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  threshold = 60,
} = {}) => {
  const startX = useRef(null);
  const startY = useRef(null);
  const swipeDelta = useRef({ x: 0, y: 0 });

  const handleTouchStart = useCallback((e) => {
    const touch = e?.touches?.[0];
    if (!touch) return;
    startX.current = touch?.clientX;
    startY.current = touch?.clientY;
    swipeDelta.current = { x: 0, y: 0 };
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e?.touches?.[0];
    if (!touch || startX?.current === null) return;
    swipeDelta.current = {
      x: touch?.clientX - startX?.current,
      y: touch?.clientY - startY?.current,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    const { x, y } = swipeDelta?.current;
    const absX = Math.abs(x);
    const absY = Math.abs(y);

    if (absX < threshold && absY < threshold) {
      // Not a swipe — reset
      startX.current = null;
      startY.current = null;
      return;
    }

    if (absX >= absY) {
      // Horizontal swipe dominates
      if (x < -threshold && onSwipeLeft) {
        triggerHaptic('medium');
        onSwipeLeft();
      } else if (x > threshold && onSwipeRight) {
        triggerHaptic('medium');
        onSwipeRight();
      }
    } else {
      // Vertical swipe dominates
      if (y < -threshold && onSwipeUp) {
        triggerHaptic('light');
        onSwipeUp();
      }
    }

    startX.current = null;
    startY.current = null;
    swipeDelta.current = { x: 0, y: 0 };
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, threshold]);

  const handlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return { handlers, swipeDelta };
};

export default useTouchSwipe;
